import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Star, Camera, X, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { z } from "zod";

const MAX_PHOTOS = 3;

const reviewSchema = z.object({
  rating: z.number().int().min(1, "Please select a rating").max(5),
  comment: z.string().trim().max(2000, "Comment too long").optional(),
});

const Review = () => {
  const { bookingId } = useParams<{ bookingId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const { settings } = useSiteSettings();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const googleUrl = settings.google_reviews_enabled === "true" ? settings.google_review_url?.trim() : "";

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth?redirect=${encodeURIComponent(`/review${bookingId ? `/${bookingId}` : ""}`)}`);
    }
  }, [user, authLoading, navigate, bookingId]);

  // Check if client has reviewed in last 30 days
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("reviews")
        .select("id")
        .eq("user_id", user.id)
        .gte("submitted_at", thirtyDaysAgo)
        .limit(1);
      setAlreadyReviewed((data?.length || 0) > 0);
      setChecking(false);
    };
    check();
  }, [user]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const total = photos.length + files.length;
    if (total > MAX_PHOTOS) {
      toast({
        title: "Too many photos",
        description: `Maximum ${MAX_PHOTOS} photos allowed.`,
        variant: "destructive",
      });
      return;
    }
    const newFiles = files.slice(0, MAX_PHOTOS - photos.length);
    const newPreviews = newFiles.map((f) => URL.createObjectURL(f));
    setPhotos([...photos, ...newFiles]);
    setPhotoPreviews([...photoPreviews, ...newPreviews]);
  };

  const removePhoto = (idx: number) => {
    URL.revokeObjectURL(photoPreviews[idx]);
    setPhotos(photos.filter((_, i) => i !== idx));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!user) return;
    const parsed = reviewSchema.safeParse({ rating, comment: comment.trim() });
    if (!parsed.success) {
      toast({
        title: "Check your review",
        description: parsed.error.issues[0]?.message || "Invalid input",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: review, error: revErr } = await supabase
        .from("reviews")
        .insert({
          user_id: user.id,
          booking_id: bookingId || null,
          rating,
          comment: comment.trim(),
        })
        .select("id")
        .single();
      if (revErr) throw revErr;

      // Upload photos
      for (const file of photos) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${user.id}/${review.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("review-photos")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) {
          console.error("Photo upload failed:", upErr);
          continue;
        }
        await supabase.from("review_photos").insert({
          review_id: review.id,
          user_id: user.id,
          file_path: path,
        });
      }

      setSubmitted(true);
      toast({ title: "Thank you!", description: "Your review has been submitted." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSubmitting(false);
  };

  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-32 pb-24">
          <div className="container mx-auto px-6 max-w-lg text-center">
            <div className="bg-card border border-primary/30 p-10">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-primary" />
              </div>
              <h1 className="font-serif text-3xl font-bold italic mb-2">Thank You!</h1>
              <p className="text-muted-foreground font-light mb-6">
                Your review means the world to us.
              </p>
              {googleUrl && (
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-primary text-xs tracking-[0.3em] uppercase hover:bg-primary/10 transition-colors"
                >
                  Also leave a Google review <ExternalLink size={12} />
                </a>
              )}
              <div className="mt-6">
                <Link to="/" className="text-xs text-muted-foreground hover:text-foreground tracking-widest uppercase">
                  ← Back to site
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-lg">
          <div className="bg-card border border-border/50 p-8 sm:p-10">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold italic mb-2 text-center">
              Leave a Review
            </h1>

            {alreadyReviewed && !showForm ? (
              <div className="mt-6 text-center space-y-6">
                <div className="p-6 bg-primary/5 border border-primary/30">
                  <CheckCircle2 size={32} className="text-primary mx-auto mb-2" />
                  <p className="font-serif italic text-lg">Review collected — thank you!</p>
                  <p className="text-sm text-muted-foreground font-light mt-1">
                    We've received a recent review from you.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setAlreadyReviewed(false);
                    setShowForm(true);
                  }}
                  variant="outline"
                  className="rounded-none px-8 py-5 text-xs tracking-[0.3em] uppercase border-primary hover:bg-primary/10"
                >
                  Submit Another
                </Button>
                {googleUrl && (
                  <div>
                    <a
                      href={googleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs text-primary hover:underline tracking-widest uppercase"
                    >
                      Leave a Google review <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <>
                <p className="text-center text-muted-foreground font-light mb-8">
                  Your feedback helps us serve you better.
                </p>

                {/* Star rating */}
                <div className="flex justify-center gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`Rate ${n} stars`}
                    >
                      <Star
                        size={36}
                        className={
                          (hover || rating) >= n
                            ? "fill-primary text-primary"
                            : "text-muted-foreground/40"
                        }
                      />
                    </button>
                  ))}
                </div>

                {/* Comment */}
                <div className="mb-6">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                    Your thoughts (optional)
                  </label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, 2000))}
                    placeholder="Tell us about your experience…"
                    rows={5}
                    className="bg-background border-border/50 rounded-none"
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">{comment.length}/2000</p>
                </div>

                {/* Photo upload */}
                <div className="mb-6">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                    Add Photos ({photos.length}/{MAX_PHOTOS})
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {photoPreviews.map((src, i) => (
                      <div key={i} className="relative aspect-square border border-border/50 overflow-hidden">
                        <img src={src} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center hover:bg-black"
                          aria-label="Remove photo"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <label className="aspect-square border border-dashed border-border/50 flex flex-col items-center justify-center cursor-pointer hover:bg-accent transition-colors">
                        <Camera size={20} className="text-muted-foreground mb-1" />
                        <span className="text-[10px] tracking-widest uppercase text-muted-foreground">Add</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handlePhotoChange}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={submitting || rating === 0}
                  className="w-full rounded-none py-6 text-xs tracking-[0.3em] uppercase"
                >
                  {submitting ? (
                    <><Loader2 size={14} className="mr-2 animate-spin" /> Submitting…</>
                  ) : (
                    "Submit Review"
                  )}
                </Button>

                {googleUrl && (
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 block w-full text-center py-3 border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors text-xs tracking-[0.25em] uppercase"
                  >
                    ⭐ Also Leave a Google Review
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Review;
